import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import dayjs from 'dayjs';
import {
  Badge,
  Container,
  Title,
  Group,
  Select,
  Card,
  Text,
  Loader,
  Center,
  Alert,
  Button,
  Drawer,
  Stack,
  SegmentedControl,
} from '@mantine/core';
import { modals } from '@mantine/modals';
import {
  IconDownload,
  IconInfoCircle,
  IconFileTypePdf,
  IconFileTypeDocx,
} from '@tabler/icons-react';
import type { CompileFormat } from '../../hooks/useEssayApi';
import {
  useEssaysSummary,
  useEssay,
  useExportEssayDocx,
  useCompileEssays,
  useSubmitEssay,
  useUnsubmitEssay,
  useReturnEssay,
  EssaySummaryRow,
} from '../../hooks/useEssayApi';
import { EssayEditor } from '../../components/essays/EssayEditor';
import { EssaySummaryTable, isSelectable } from '../../components/essays/EssaySummaryTable';
import { reportingPeriod } from '../../components/essays/essayPeriod';
import { QuarterlyManage } from './QuarterlyManage';
import { useCompanyModules } from '../../hooks/useCompanyModules';
import { hasGrantOnOthers, useMyPermissions } from '../../hooks/usePermission';
import { Error403 } from '../../components/errors/ErrorPage';
import { useDevice } from '../../hooks/useDevice';

export const EssaysManage: React.FC = () => {
  const { t, i18n } = useTranslation();
  const lang = i18n.language;
  const isMobile = useDevice() === 'mobile';
  // isPending, а не isLoading: пока профиль не загружен, запрос выключен — это ещё не «нет прав».
  const { data: perms, isPending: isPermsPending } = useMyPermissions();
  // Вкладка неподключённого компании раздела не показывается, как вкладка без права.
  const { hasModule } = useCompanyModules();
  const canManage = hasModule('essays') && hasGrantOnOthers(perms, 'essays.read');
  // Права разные (месячные — essays.read, квартальные — quarterly.read). Пустить на страницу
  // надо по любому из двух, иначе тот, у кого есть только квартальная сводка, упрётся в 403.
  const canManageQuarterly = hasModule('quarterly') && hasGrantOnOthers(perms, 'quarterly.read');

  const now = dayjs();
  // Не текущий месяц, а отчётный — см. reportingPeriod.
  const [month, setMonth] = useState(() => String(reportingPeriod().month));
  const [year, setYear] = useState(() => String(reportingPeriod().year));
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [openedEssayId, setOpenedEssayId] = useState<string | null>(null);
  const [mode, setMode] = useState<'month' | 'quarter'>('month');

  const { data: rows, isLoading: isSummaryLoading } = useEssaysSummary(
    Number(year),
    Number(month),
    canManage,
  );
  const { compile, compilingFormat } = useCompileEssays();

  // Дефолтный отбор (всё сданное и непустое) применяется один раз на пару
  // месяц+год. Иначе любой рефетч сводки — например после смены статуса из
  // drawer — затирал бы галочки, расставленные руками.
  const summaryKey = `${year}-${month}`;
  const appliedKeyRef = useRef<string | null>(null);
  const isSelectionStale = appliedKeyRef.current !== summaryKey;
  useEffect(() => {
    if (!rows || appliedKeyRef.current === summaryKey) return;
    appliedKeyRef.current = summaryKey;
    setSelectedIds(
      new Set(rows.filter((r) => r.status === 'submitted' && !r.isEmpty).map((r) => r.userId)),
    );
  }, [rows, summaryKey]);

  // Пока сводка за новый месяц не пришла, selectedIds ещё описывает старый —
  // собрать по нему отчёт значит выдать чужой месяц. Кнопку держим выключенной.
  const selectedCount = isSelectionStale ? 0 : selectedIds.size;

  const monthOptions = useMemo(
    () =>
      Array.from({ length: 12 }, (_, i) => ({
        value: String(i + 1),
        label: dayjs()
          .locale(lang || 'ru')
          .month(i)
          .format('MMMM'),
      })),
    [lang],
  );
  const yearOptions = useMemo(() => {
    const y = now.year();
    return Array.from({ length: 5 }, (_, i) => String(y - 3 + i));
  }, [now]);

  const submittedCount = (rows || []).filter((r) => r.status === 'submitted').length;
  const draftsSelected = (rows || []).filter(
    (r) => selectedIds.has(r.userId) && r.status === 'draft',
  ).length;

  const toggle = (userId: string) =>
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(userId)) next.delete(userId);
      else next.add(userId);
      return next;
    });

  const toggleAll = () => {
    const selectable = (rows || []).filter(isSelectable);
    setSelectedIds((prev) =>
      selectable.every((r) => prev.has(r.userId))
        ? new Set()
        : new Set(selectable.map((r) => r.userId)),
    );
  };

  const runCompile = (format: CompileFormat) =>
    compile({
      year: Number(year),
      month: Number(month),
      userIds: Array.from(selectedIds),
      format,
    });

  const handleCompile = (format: CompileFormat) => {
    if (draftsSelected === 0) return runCompile(format);
    modals.openConfirmModal({
      title: t('essays.compileDraftsTitle'),
      centered: true,
      children: (
        <Text size="sm">{t('essays.compileDraftsWarning', { count: draftsSelected })}</Text>
      ),
      labels: {
        confirm: t(format === 'docx' ? 'essays.compileDocx' : 'essays.compilePdf'),
        cancel: t('common.cancel'),
      },
      onConfirm: () => runCompile(format),
    });
  };

  if (isPermsPending) {
    return (
      <Center py="xl">
        <Loader />
      </Center>
    );
  }
  if (!canManage && !canManageQuarterly) return <Error403 />;

  // Тому, у кого есть только квартальные, месячную вкладку не показываем: она
  // всё равно вернула бы 403 на каждый запрос.
  const activeMode = canManage ? mode : 'quarter';

  return (
    <Container size="lg" py="md">
      <Group justify="space-between" mb="lg" wrap="wrap">
        <Group gap="xs" align="center">
          <Title order={2}>{t('essays.manageTitle')}</Title>
          <Badge size="xs" variant="outline">
            {t('common.beta')}
          </Badge>
        </Group>
        {canManage && canManageQuarterly && (
          <SegmentedControl
            size="xs"
            value={activeMode}
            onChange={(v) => setMode(v as 'month' | 'quarter')}
            data={[
              { value: 'month', label: t('quarterly.monthTab') },
              { value: 'quarter', label: t('quarterly.tab') },
            ]}
          />
        )}
      </Group>

      {activeMode === 'quarter' && canManageQuarterly && <QuarterlyManage />}

      {activeMode === 'month' && (
        <Card withBorder radius="md" padding="md" mb="md">
          <Group justify="space-between" align="flex-end" wrap="wrap">
            <Group wrap="wrap">
              <Select
                label={t('essays.month')}
                data={monthOptions}
                value={month}
                onChange={(v) => v && setMonth(v)}
                allowDeselect={false}
                comboboxProps={{ withinPortal: true }}
              />
              <Select
                label={t('essays.year')}
                data={yearOptions}
                value={year}
                onChange={(v) => v && setYear(v)}
                allowDeselect={false}
                comboboxProps={{ withinPortal: true }}
              />
            </Group>
            <Group>
              <Text c="dimmed" size="sm">
                {t('essays.summaryCount', {
                  submitted: submittedCount,
                  total: (rows || []).length,
                })}
              </Text>
              <Button
                leftSection={<IconFileTypePdf size={16} />}
                disabled={selectedCount === 0}
                loading={compilingFormat === 'pdf'}
                onClick={() => handleCompile('pdf')}
              >
                {t('essays.compileSelected', { count: selectedCount })}
              </Button>
              {/* DOCX — запасной формат для тех, кто ещё не перешёл на PDF:
                тот же счётчик на второй кнопке читался бы как второе действие. */}
              <Button
                variant="subtle"
                leftSection={<IconFileTypeDocx size={16} />}
                disabled={selectedCount === 0}
                loading={compilingFormat === 'docx'}
                onClick={() => handleCompile('docx')}
              >
                {t('essays.compileDocx')}
              </Button>
            </Group>
          </Group>
        </Card>
      )}

      {activeMode === 'month' &&
        (isSummaryLoading ? (
          <Center py="xl">
            <Loader />
          </Center>
        ) : !rows || rows.length === 0 ? (
          <Alert icon={<IconInfoCircle size={16} />} color="gray" variant="light">
            {t('essays.noPeopleForMonth')}
          </Alert>
        ) : (
          <EssaySummaryTable
            rows={rows}
            selectedIds={selectedIds}
            onToggle={toggle}
            onToggleAll={toggleAll}
            onOpen={(row: EssaySummaryRow) => row.essayId && setOpenedEssayId(row.essayId)}
          />
        ))}

      <Drawer
        opened={!!openedEssayId}
        onClose={() => setOpenedEssayId(null)}
        position={isMobile ? 'bottom' : 'right'}
        size={isMobile ? '90%' : 'xl'}
        title={t('essays.manageTitle')}
      >
        {openedEssayId && <ManagedEssayPanel essayId={openedEssayId} />}
      </Drawer>
    </Container>
  );
};

const ManagedEssayPanel: React.FC<{ essayId: string }> = ({ essayId }) => {
  const { t } = useTranslation();
  const { data, isLoading, isError } = useEssay(essayId);
  const { exportEssay, isExporting } = useExportEssayDocx();
  // Ошибку смены статуса показывает сам хук — второй тост здесь не нужен.
  const submitEssay = useSubmitEssay();
  const unsubmitEssay = useUnsubmitEssay();
  const returnEssay = useReturnEssay();

  if (isLoading) {
    return (
      <Center py="xl">
        <Loader />
      </Center>
    );
  }
  // Эссе могли удалить между загрузкой сводки и открытием drawer. Без этой
  // ветки неуспешный запрос крутил бы спиннер вечно.
  if (isError || !data) {
    return (
      <Alert icon={<IconInfoCircle size={16} />} color="gray" variant="light">
        {t('essays.noEssayForMonth')}
      </Alert>
    );
  }

  return (
    <Stack gap="md">
      <Group justify="space-between" align="center">
        <Text c="dimmed" size="sm">
          {data.canEdit ? t('essays.editingAllowed') : t('essays.readOnlyNotice')}
        </Text>
        <Button
          variant="light"
          color="teal"
          size="sm"
          leftSection={<IconDownload size={16} />}
          onClick={() => exportEssay(data.essay._id)}
          loading={isExporting}
        >
          {t('essays.exportDocx')}
        </Button>
      </Group>
      <EssayEditor
        // Смена статуса меняет версию эссе — редактор пересобирается на новой.
        key={`${data.essay._id}:${data.essay.status}`}
        essayId={data.essay._id}
        initialContent={data.essay}
        readOnly={!data.canEdit}
        status={data.essay.status}
        importedAt={data.essay.importedAt}
        updatedAt={data.essay.updatedAt}
        canSubmit={data.canSubmit}
        returnedForRework={data.essay.returnedForRework}
        canReturn={data.canReturn}
        isReturnPending={returnEssay.isPending}
        onReturn={(comment) => returnEssay.mutateAsync({ id: data.essay._id, comment })}
        isStatusPending={submitEssay.isPending || unsubmitEssay.isPending}
        onSubmit={() => submitEssay.mutate(data.essay._id)}
        onUnsubmit={() => unsubmitEssay.mutate(data.essay._id)}
      />
    </Stack>
  );
};
